import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Checkbox,
  IconButton,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  CloseOutlined,
  ContentCopyOutlined,
  FileDownloadOutlined,
  FilterAlt,
  FirstPage,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  LastPage,
  PictureAsPdfOutlined,
  SearchRounded,
  VisibilityOutlined,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import Breadcrumb from "../../../layouts/full-layout/breadcrumb/Breadcrumb";
import PageContainer from "../../../components/container/PageContainer";
import { GetTenHoursReport } from "../../../services/asai";
import { downloadReportPdf } from "./reportPdf";

type EmployeeType = "payroll" | "contract";

type ReportRow = {
  personId: string;
  personName: string;
  shift: string;
  status: string;
  hours: string;
  hoursStatus: string;
};

type ColumnKey = keyof ReportRow;

const COLUMNS: Array<{ key: ColumnKey; label: string }> = [
  { key: "personId", label: "ID" },
  { key: "personName", label: "Person Name" },
  { key: "shift", label: "Shift" },
  { key: "status", label: "Status" },
  { key: "hours", label: "Hours" },
  { key: "hoursStatus", label: "Hours Status" },
];

const EMPLOYEE_TABS: Array<{
  value: EmployeeType;
  label: string;
}> = [
  { value: "payroll", label: "Payroll Offday Report" },
  { value: "contract", label: "Contract Offday Report" },
];

const PAGE_TITLES: Record<EmployeeType, string> = {
  payroll: "10 Hours Payroll with Offday Report",
  contract: "10 Hours Contract with Offday Report",
};

const parseRows = (response: any): ReportRow[] => {
  const result =
    response?.data?.data ??
    response?.data ??
    {};

  const records = Array.isArray(result?.result)
    ? result.result
    : Array.isArray(result?.Result)
      ? result.Result
      : [];

  return records
    .filter((item: any) => {
      const status = String(
        item?.hoursStatus ??
          item?.HoursStatus ??
          item?.gender ??
          "",
      ).trim().toLowerCase();

      return status === "off day";
    })
    .map((item: any) => ({
      personId: String(
        item?.personId ??
          item?.PersonId ??
          item?.personid ??
          "",
      ),
      personName: String(
        item?.personName ??
          item?.PersonName ??
          item?.name ??
          "",
      ),
      shift: String(
        item?.shift ??
          item?.Shift ??
          item?.Organization ??
          "",
      ),
      status: String(
        item?.status ??
          item?.Status ??
          "-",
      ),
      hours: String(
        item?.hours ??
          item?.Hours ??
          "-",
      ),
      hoursStatus: String(
        item?.hoursStatus ??
          item?.HoursStatus ??
          item?.gender ??
          "",
      ),
    }));
};

const getToday = () =>
  new Date().toISOString().slice(0, 10);


const TablePaginationActions = ({
  count,
  page,
  rowsPerPage,
  onPageChange,
}: {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    page: number,
  ) => void;
}) => {
  const lastPage = Math.max(
    0,
    Math.ceil(count / rowsPerPage) - 1,
  );

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        size="small"
        onClick={(event) => onPageChange(event, 0)}
        disabled={page === 0}
      >
        <FirstPage fontSize="small" />
      </IconButton>

      <IconButton
        size="small"
        onClick={(event) => onPageChange(event, page - 1)}
        disabled={page === 0}
      >
        <KeyboardArrowLeft fontSize="small" />
      </IconButton>

      <IconButton
        size="small"
        onClick={(event) =>
          onPageChange(event, page + 1)
        }
        disabled={page >= lastPage}
      >
        <KeyboardArrowRight fontSize="small" />
      </IconButton>

      <IconButton
        size="small"
        onClick={(event) =>
          onPageChange(event, lastPage)
        }
        disabled={page >= lastPage}
      >
        <LastPage fontSize="small" />
      </IconButton>
    </Box>
  );
};

const OffdayReportPage: React.FC = () => {
  const [employeeType, setEmployeeType] =
    useState<EmployeeType>("payroll");

  const [selectedDate, setSelectedDate] =
    useState(getToday());

  const [rows, setRows] =
    useState<ReportRow[]>([]);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  // Device Data-style table controls.
  const [tableSearch, setTableSearch] = useState("");
  const [columnFilters, setColumnFilters] =
    useState<Partial<Record<ColumnKey, string>>>({});
  const [showAllFilters, setShowAllFilters] = useState(false);
  const [columnAnchorEl, setColumnAnchorEl] =
    useState<null | HTMLElement>(null);
  const [visibleColumns, setVisibleColumns] =
    useState<Record<ColumnKey, boolean>>({
      personId: true,
      personName: true,
      shift: true,
      status: true,
      hours: true,
      hoursStatus: true,
    });

  const [orderDirection, setOrderDirection] =
    useState<"asc" | "desc">("asc");
  const [valueToOrderBy, setValueToOrderBy] =
    useState<ColumnKey>("personId");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const activeColumnCount = COLUMNS.filter(
    (column) => visibleColumns[column.key],
  ).length;

  const hasColumnFilters = Object.values(columnFilters).some(
    (value) => String(value ?? "").trim() !== "",
  );

  const columnFilterCount = Object.values(columnFilters).filter(
    (value) => String(value ?? "").trim() !== "",
  ).length;

  const loadReport = React.useCallback(async (
    type: EmployeeType,
    date: string,
  ) => {
    if (!date) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response =
        await GetTenHoursReport({
          employeeType: type,
          date,
        });

      setRows(parseRows(response));
      setPage(0);
    } catch (requestError: any) {
      console.error(
        "Failed to load 10-hour off-day report:",
        requestError,
      );

      setRows([]);
      setError(
        requestError?.response?.data?.message ||
          requestError?.message ||
          "Unable to load the off-day report.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadReport(employeeType, selectedDate);
  }, [employeeType, loadReport]);

  const filteredRows = useMemo(() => {
    let data = [...rows];

    const globalSearch = tableSearch.trim().toLowerCase();

    if (globalSearch) {
      data = data.filter((row) =>
        Object.values(row)
          .map((value) => String(value ?? ""))
          .join(" ")
          .toLowerCase()
          .includes(globalSearch),
      );
    }

    COLUMNS.forEach((column) => {
      const field = column.key;
      const fieldSearch = String(
        columnFilters[field] ?? "",
      )
        .trim()
        .toLowerCase();

      if (!fieldSearch) {
        return;
      }

      data = data.filter((row) =>
        String(row[field] ?? "")
          .toLowerCase()
          .includes(fieldSearch),
      );
    });

    data.sort((first, second) => {
      const firstValue = String(first[valueToOrderBy] ?? "");
      const secondValue = String(second[valueToOrderBy] ?? "");

      const comparison = firstValue.localeCompare(
        secondValue,
        undefined,
        {
          numeric: true,
          sensitivity: "base",
        },
      );

      return orderDirection === "asc"
        ? comparison
        : -comparison;
    });

    return data;
  }, [
    rows,
    tableSearch,
    columnFilters,
    valueToOrderBy,
    orderDirection,
  ]);

  const visibleRows = useMemo(
    () =>
      filteredRows.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [filteredRows, page, rowsPerPage],
  );

  useEffect(() => {
    const lastPage = Math.max(
      0,
      Math.ceil(filteredRows.length / rowsPerPage) - 1,
    );

    if (page > lastPage) {
      setPage(lastPage);
    }
  }, [filteredRows.length, page, rowsPerPage]);

  const activeEmployeeLabel =
    EMPLOYEE_TABS.find(
      (tab) => tab.value === employeeType,
    )?.label ?? "";

  const breadcrumbItems = [
    { to: "/", title: "ASAI" },
    { title: "10 Hours Shift Reports" },
    { title: "Off Day Report" },
    { title: activeEmployeeLabel },
  ];

  const exportExcel = () => {
    const data = filteredRows.map((row) => {
      const record: Record<string, string> = {};

      COLUMNS.filter(
        (column) => visibleColumns[column.key],
      ).forEach((column) => {
        record[column.label] = String(row[column.key] ?? "");
      });

      return record;
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Off Day Report",
    );

    XLSX.writeFile(
      workbook,
      `${PAGE_TITLES[employeeType].replace(/\s+/g, "_")}_${selectedDate}.xlsx`,
    );
  };

  const exportPdf = () => {
    downloadReportPdf({
      title: PAGE_TITLES[employeeType],
      date: selectedDate,
      columns: COLUMNS.filter(
        (column) => visibleColumns[column.key],
      ).map((column) => ({
        header: column.label,
        key: column.key,
      })),
      rows: filteredRows,
      fileName: `${PAGE_TITLES[employeeType].replace(/\s+/g, "_")}_${selectedDate}.pdf`,
    });
  };

  const copyRows = async () => {
    const columns = COLUMNS.filter(
      (column) => visibleColumns[column.key],
    );

    const header = columns
      .map((column) => column.label)
      .join("\t");

    const body = filteredRows
      .map((row) =>
        columns
          .map((column) => String(row[column.key] ?? ""))
          .join("\t"),
      )
      .join("\n");

    try {
      await navigator.clipboard.writeText(`${header}\n${body}`);
    } catch {
      setError("Unable to copy the report.");
    }
  };

  const handleSort = (field: ColumnKey) => {
    const isAsc =
      valueToOrderBy === field &&
      orderDirection === "asc";

    setValueToOrderBy(field);
    setOrderDirection(isAsc ? "desc" : "asc");
    setPage(0);
  };

  const toggleColumn = (field: ColumnKey) => {
    if (
      visibleColumns[field] &&
      activeColumnCount === 1
    ) {
      return;
    }

    setVisibleColumns((previous) => ({
      ...previous,
      [field]: !previous[field],
    }));
  };

  const clearColumnFilter = (field: ColumnKey) => {
    setColumnFilters((previous) => {
      const next = { ...previous };
      delete next[field];
      return next;
    });

    setPage(0);
  };

  const isColumnFiltered = (field: ColumnKey) =>
    Boolean(columnFilters[field]?.trim());

  const resetTableFilters = () => {
    setTableSearch("");
    setColumnFilters({});
    setShowAllFilters(false);
    setPage(0);
  };

  return (
    <PageContainer
      title={PAGE_TITLES[employeeType]}
    >
      <Breadcrumb
        title={PAGE_TITLES[employeeType]}
        items={breadcrumbItems}
      />

      <Box
        sx={{
          width: "100%",
          px: { xs: 1.5, sm: 2, md: 3 },
          py: { xs: 1.5, md: 2.5 },
        }}
      >
        <Card
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1.5,
          }}
        >
          <Box
            sx={{
              borderBottom: "1px solid",
              borderColor: "divider",
              px: 2,
            }}
          >
            <Tabs
              value={employeeType}
              onChange={(_, value) =>
                setEmployeeType(value)
              }
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              {EMPLOYEE_TABS.map((tab) => (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  label={tab.label}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                />
              ))}
            </Tabs>
          </Box>

          <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "end",
                justifyContent: "space-between",
                gap: 1.5,
                mb: 2,
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "end",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  label="Date"
                  type="date"
                  value={selectedDate}
                  onChange={(event) => {
                    setSelectedDate(event.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ width: 250 }}
                />

                <Button
                  variant="contained"
                  onClick={() => void loadReport(employeeType, selectedDate)}
                  disabled={loading || !selectedDate}
                  sx={{
                    minHeight: 40,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Search
                </Button>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 0.75,
                  flexWrap: "wrap",
                  ml: "auto",
                }}
              >
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<ContentCopyOutlined />}
                  onClick={() => void copyRows()}
                  disabled={!filteredRows.length}
                  sx={{ textTransform: "none" }}
                >
                  Copy
                </Button>

                <Button
                  size="small"
                  variant="contained"
                  startIcon={<FileDownloadOutlined />}
                  onClick={exportExcel}
                  disabled={!filteredRows.length}
                  sx={{ textTransform: "none" }}
                >
                  Excel
                </Button>

                <Button
                  size="small"
                  variant="contained"
                  startIcon={<PictureAsPdfOutlined />}
                  onClick={exportPdf}
                  disabled={!filteredRows.length}
                  sx={{ textTransform: "none" }}
                >
                  PDF
                </Button>

                <TextField
                  size="small"
                  placeholder="Search..."
                  value={tableSearch}
                  onChange={(event) => {
                    setTableSearch(event.target.value);
                    setPage(0);
                  }}
                  sx={{
                    width: { xs: "100%", sm: 200 },
                    backgroundColor: "background.paper",
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRounded fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                <FilterAlt
                  sx={{
                    cursor: "pointer",
                    color: showAllFilters
                      ? "primary.main"
                      : "text.primary",
                  }}
                  onClick={() => {
                    setShowAllFilters((current) => !current);
                    setTableSearch("");
                    setPage(0);
                  }}
                />

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<VisibilityOutlined />}
                  onClick={(event) =>
                    setColumnAnchorEl(event.currentTarget)
                  }
                  sx={{
                    textTransform: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  Columns
                  {COLUMNS.length - activeColumnCount > 0 &&
                    ` (${COLUMNS.length - activeColumnCount})`}
                </Button>

                <Menu
                  anchorEl={columnAnchorEl}
                  open={Boolean(columnAnchorEl)}
                  onClose={() => setColumnAnchorEl(null)}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                >
                  {COLUMNS.map((column) => (
                    <MenuItem
                      key={column.key}
                      onClick={() => toggleColumn(column.key)}
                    >
                      <Checkbox
                        size="small"
                        checked={visibleColumns[column.key]}
                      />
                      <ListItemText primary={column.label} />
                    </MenuItem>
                  ))}
                </Menu>

                <Button
                  size="small"
                  variant="outlined"
                  disabled={
                    !tableSearch.trim() &&
                    !hasColumnFilters
                  }
                  onClick={resetTableFilters}
                  sx={{
                    textTransform: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  Reset Filter
                </Button>
              </Box>
            </Box>

            {error && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
              >
                {error}
              </Alert>
            )}


            <Typography
              sx={{
                mb: 1,
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {filteredRows.length} employees
            </Typography>

            {loading ? (
              <Box
                sx={{
                  py: 8,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={30} />
              </Box>
            ) : (
              <>
                <TableContainer
                  component={Paper}
                  sx={{
                    maxHeight: showAllFilters ? 540 : 440,
                    overflowY:
                      filteredRows.length > 5 ? "auto" : "visible",
                    overflowX: "auto",
                    scrollbarGutter: "stable",
                  }}
                >
                  <Table
                    aria-label="off day report table"
                    stickyHeader
                    sx={{
                      whiteSpace: "nowrap",
                      overflowX: {
                        md: "scroll",
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        {COLUMNS.filter(
                          (column) => visibleColumns[column.key],
                        ).map((column) => (
                          <TableCell key={column.key}>
                            <TableSortLabel
                              active={valueToOrderBy === column.key}
                              direction={
                                valueToOrderBy === column.key
                                  ? orderDirection
                                  : "desc"
                              }
                              onClick={() => handleSort(column.key)}
                            >
                              {column.label}
                            </TableSortLabel>
                          </TableCell>
                        ))}
                      </TableRow>

                      {showAllFilters && (
                        <TableRow>
                          {COLUMNS.filter(
                            (column) => visibleColumns[column.key],
                          ).map((column) => (
                            <TableCell key={column.key}>
                              <TextField
                                size="small"
                                fullWidth
                                placeholder="Search..."
                                value={columnFilters[column.key] || ""}
                                onChange={(event) => {
                                  const value = event.target.value;

                                  setColumnFilters((previous) => {
                                    const updated = { ...previous };

                                    if (value.trim()) {
                                      updated[column.key] = value;
                                    } else {
                                      delete updated[column.key];
                                    }

                                    return updated;
                                  });

                                  setPage(0);
                                }}
                                InputProps={{
                                  endAdornment:
                                    isColumnFiltered(column.key) ? (
                                      <InputAdornment position="end">
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            clearColumnFilter(column.key)
                                          }
                                        >
                                          <CloseOutlined fontSize="small" />
                                        </IconButton>
                                      </InputAdornment>
                                    ) : undefined,
                                }}
                              />
                            </TableCell>
                          ))}
                        </TableRow>
                      )}
                    </TableHead>

                    <TableBody>
                      {loading && (
                        <TableRow>
                          <TableCell
                            colSpan={Math.max(activeColumnCount, 1)}
                            align="center"
                            sx={{ py: 5 }}
                          >
                            <CircularProgress size={28} />
                          </TableCell>
                        </TableRow>
                      )}

                      {!loading && filteredRows.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={Math.max(activeColumnCount, 1)}
                            align="center"
                            sx={{
                              py: 7,
                              color: "text.secondary",
                            }}
                          >
                            {rows.length
                              ? "No records match your filters."
                              : "No off-day records found."}
                          </TableCell>
                        </TableRow>
                      )}

                      {!loading &&
                        visibleRows.map((row, index) => (
                          <TableRow
                            key={`${row.personId}-${row.personName}-${index}`}
                          >
                            {COLUMNS.filter(
                              (column) => visibleColumns[column.key],
                            ).map((column) => (
                              <TableCell key={column.key}>
                                {row[column.key] || "-"}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                    </TableBody>

                    <TableFooter>
                      <TableRow>
                        <TableCell />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <TablePagination
                    component="div"
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    count={filteredRows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(event) => {
                      setRowsPerPage(
                        parseInt(event.target.value, 10),
                      );
                      setPage(0);
                    }}
                    ActionsComponent={TablePaginationActions}
                    labelRowsPerPage="Rows per page:"
                  />
                </Box>
              </>
            )}
          </Box>
        </Card>
      </Box>
    </PageContainer>
  );
};

export default OffdayReportPage;
